import { activeAgent, formatClock, linkPhase } from '@krp/client-runtime'

import { modeWord } from './link'
import type { DriverCategory, DriverState, DriverStatus, EquipmentSnapshot } from './types'

/**
 * Вкладка «Драйверы»: единый список драйверов (п. 2.2, 5.2). Агент нового уровня отдаёт его сам (`GET /v1/drivers`).
 */

export const ATOL_DRIVER_ID = 'atol-kkt'
export const INPAS_DRIVER_ID = 'inpas-dual-connector'

/** Драйверы, строки которых есть в окне всегда (даже если агент про них не сообщил — тогда «не проверялось»). */
const EXPECTED: readonly { id: string; category: DriverCategory; title: string }[] = [
  { id: ATOL_DRIVER_ID, category: 'fiscal-register', title: 'Драйвер ККТ 10 (АТОЛ)' },
  { id: INPAS_DRIVER_ID, category: 'payment-terminal', title: 'INPAS Dual Connector' },
]

/** Список драйверов, которому окно верит: ответ `/v1/drivers`; без него списка нет. */
export function resolveDrivers(snapshot: EquipmentSnapshot): readonly DriverStatus[] {
  return snapshot.drivers ?? []
}

export function findDriver(snapshot: EquipmentSnapshot, id: string): DriverStatus | null {
  return resolveDrivers(snapshot).find((driver) => driver.id === id) ?? null
}

/** Драйвер ККТ готов? `false` и когда он не установлен, и когда про него ещё ничего не известно. */
export const atolDriverReady = (snapshot: EquipmentSnapshot): boolean => findDriver(snapshot, ATOL_DRIVER_ID)?.state === 'ready'

export type DriverRowState = DriverState | 'unknown'
export type DriverTone = 'ok' | 'warn' | 'block' | 'wait'
export type DriverAction = 'download' | 'check'

export interface DriverRowVm {
  id: string
  category: DriverCategory
  title: string
  state: DriverRowState
  word: string
  glyph: '✓' | '!' | '✕' | '–'
  tone: DriverTone
  version: string | null
  path: string | null
  /** Слова агента дословно или наше «проверим, когда программа заработает». */
  text: string
  /** Нужен для работы окна прямо сейчас (касса — всегда; драйвер терминала — когда такой терминал настроен). */
  required: boolean
  /** Ставить ли «Скачать» (драйвер не готов и это не неизвестность). */
  action: DriverAction | null
}

const WORDS: Record<DriverRowState, { word: string; glyph: DriverRowVm['glyph']; tone: DriverTone }> = {
  ready: { word: 'Готов', glyph: '✓', tone: 'ok' },
  'not-installed': { word: 'Не установлен', glyph: '✕', tone: 'block' },
  stopped: { word: 'Не запущен', glyph: '!', tone: 'warn' },
  incompatible: { word: 'Не подходит', glyph: '✕', tone: 'block' },
  unsupported: { word: 'Не поддерживается', glyph: '!', tone: 'warn' },
  unknown: { word: 'Не проверялось', glyph: '–', tone: 'wait' },
}

/** Драйвер нужен сейчас: касса — всегда, драйвер терминала — когда по нему настроен терминал. */
function isRequired(snapshot: EquipmentSnapshot, driver: { id: string; category: DriverCategory }): boolean {
  if (driver.category === 'fiscal-register') return true
  if (driver.category === 'payment-terminal') return (snapshot.terminals?.items ?? []).some((item) => item.kind === driver.id)
  return false
}

const WAITING_TEXT = 'Проверим, когда программа-драйвер заработает.'

/** Строки вкладки «Драйверы»: нужные всегда, остальные — что вернул агент. */
export function driverRows(snapshot: EquipmentSnapshot): readonly DriverRowVm[] {
  const phase = linkPhase(snapshot.link, snapshot.now)
  // При потерянной связи остаются последние известные данные (экран пометит их серыми).
  const connected = phase === 'connected' || phase === 'lost' || phase === 'outdated'
  const known = connected ? resolveDrivers(snapshot) : []
  const rows: DriverRowVm[] = EXPECTED.map((expected) => {
    const found = known.find((driver) => driver.id === expected.id)
    return rowOf(snapshot, found ?? null, expected, connected)
  })
  for (const driver of known) {
    if (EXPECTED.some((expected) => expected.id === driver.id)) continue
    rows.push(rowOf(snapshot, driver, driver, connected))
  }
  return rows
}

function rowOf(
  snapshot: EquipmentSnapshot,
  driver: DriverStatus | null,
  base: { id: string; category: DriverCategory; title: string },
  connected: boolean,
): DriverRowVm {
  const state: DriverRowState = driver?.state ?? 'unknown'
  const words = WORDS[state]
  const text = driver !== null && driver.message.length > 0 ? driver.message : driver === null ? (connected ? 'Агент про этот драйвер не сообщил.' : WAITING_TEXT) : ''
  return {
    id: base.id,
    category: base.category,
    title: driver?.title ?? base.title,
    state,
    word: words.word,
    glyph: words.glyph,
    tone: words.tone,
    version: driver?.version ?? null,
    path: driver?.path ?? null,
    text,
    required: isRequired(snapshot, base),
    action: state === 'unknown' ? null : state === 'ready' ? null : state === 'unsupported' ? null : state === 'stopped' ? 'check' : 'download',
  }
}

/** Сколько драйверов требуют действия человека (число на табе «Драйверы»). */
export function driversNeedingAction(snapshot: EquipmentSnapshot): number {
  return driverRows(snapshot).filter((row) => row.required && row.state !== 'ready' && row.state !== 'unknown').length
}

export interface AgentCardVm {
  /** `installed` — агент отвечает (или отвечал); `missing` — не найден; `unknown` — ещё ищем. */
  state: 'installed' | 'missing' | 'unknown'
  word: string
  glyph: '✓' | '✕' | '…'
  version: string | null
  port: number | null
  modeWord: string | null
  startedAt: string | null
  machine: string | null
  instanceId: string | null
  stale: boolean
}

/** Карточка агента на вкладке «Драйверы». */
export function agentCard(snapshot: EquipmentSnapshot): AgentCardVm {
  const phase = linkPhase(snapshot.link, snapshot.now)
  const agent = activeAgent(snapshot.link) ?? snapshot.link.selected
  if (phase === 'searching') {
    return { state: 'unknown', word: 'Ищем', glyph: '…', version: null, port: null, modeWord: null, startedAt: null, machine: null, instanceId: null, stale: false }
  }
  if (agent === null || phase === 'missing') {
    return { state: 'missing', word: 'Не установлена', glyph: '✕', version: null, port: null, modeWord: null, startedAt: null, machine: null, instanceId: null, stale: false }
  }
  return {
    state: 'installed',
    word: 'Установлена',
    glyph: '✓',
    version: agent.hello.version,
    port: agent.port,
    modeWord: modeWord(agent),
    startedAt: formatClock(agent.hello.host?.startedAt ?? null),
    machine: agent.hello.host?.machine ?? null,
    instanceId: agent.hello.instanceId.length > 0 ? agent.hello.instanceId : null,
    stale: phase === 'lost',
  }
}

/** Текст для кнопки «Скопировать сведения» (раздел «Сведения для поддержки»): без токенов и секретов. */
export function supportInfo(snapshot: EquipmentSnapshot): string {
  const lines: string[] = []
  const phase = linkPhase(snapshot.link, snapshot.now)
  const agent = activeAgent(snapshot.link) ?? snapshot.link.selected
  lines.push(`Сайт: ${snapshot.link.siteOrigin}`)
  lines.push(`Связь с агентом: ${phase}`)
  if (agent !== null) {
    lines.push(`Агент: версия ${agent.hello.version}, порт ${agent.port}, уровень API ${agent.hello.api?.level ?? 0}`)
    const host = agent.hello.host
    if (host !== null) lines.push(`Запуск: ${host.mode ?? '?'}, ${host.machine ?? '?'}, ${host.os ?? '?'}`)
  }
  for (const row of driverRows(snapshot)) {
    lines.push(`Драйвер ${row.id}: ${row.state}${row.version === null ? '' : ` ${row.version}`}`)
  }
  return lines.join('\n')
}

/** Версия `candidate` новее `current`? Сравнение по числам через точку; нечитаемое — «нет» (не пугаем зря). */
export function isNewerVersion(candidate: string | null, current: string | null): boolean {
  if (candidate === null || current === null) return false
  const parse = (value: string): number[] | null => {
    const parts = value.trim().split(/[.+-]/).slice(0, 4).map((part) => Number(part))
    return parts.length > 0 && parts.every((part) => Number.isInteger(part) && part >= 0) ? parts : null
  }
  const left = parse(candidate)
  const right = parse(current)
  if (left === null || right === null) return false
  for (let index = 0; index < Math.max(left.length, right.length); index++) {
    const difference = (left[index] ?? 0) - (right[index] ?? 0)
    if (difference !== 0) return difference > 0
  }
  return false
}
